import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { API_PREFIX as API, API_ROUTES } from '../../constants/api_routes';
import { makeStyles, Container, Grid, IconButton, Typography } from '@material-ui/core';
import { AddBox } from '@material-ui/icons';
import { useFormContext } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import Autocomplete from '../ui_components/autocomplete_controlled';
import TextInput from '../ui_components/text_input';
import CheckboxInput from '../ui_components/checkbox_input';
import DatePicker from '../ui_components/date_picker';
import DateTimePicker from '../ui_components/datetime_picker';
import FormShortcut from '../ui_components/form_shortcut';
import CustomerComponent from '../customer/customer';
import { set, addMinutes, isValid } from 'date-fns';
import { roundNumber } from '../../utils/helper_functions';
import { RecentActors } from '@material-ui/icons';
import DigitalRecordModal from '../customer/digital_record_modal';
import { getData } from '../../http/request';
import {ENTRYPOINT} from "../../config/entrypoint";

const useStyles = makeStyles((theme) => ({
	container: {
        width: 'auto',
        padding: theme.spacing(2, 5),
        '& p': {
            color: '#bf1650',
            '&::before': {
                display: 'inline',
                content: '"⚠ "'
            }
        }
	},
    inlineFlex: {
        display: 'inline-flex'
    },
    iconButton: {
        padding: theme.spacing(0),
        marginTop: theme.spacing(3)
    },
    iconButtonLabel: {
        display: 'flex',
        flexDirection: 'column'
    },
    lsSm: {
        '& label': {
            letterSpacing: '-0.3px'
        }
    }
}));

const getCurrentTime = () => {
    const now = new Date();
    return {
        hours: now.getHours(),
        minutes: now.getMinutes()
    }
}

const validateDate = (date) => {
    return isValid(date) || 'Nepravilan datum';
}

const BasicLayout = ({ appointmentData, currentViewName, isDoctor }) => {
    const { control, setValue, getValues, formState: { errors }, reset } = useFormContext();
    const { container, inlineFlex, iconButton, iconButtonLabel, lsSm } = useStyles();
    const [startDate, setStartDate] = useState(new Date(appointmentData?.startDate));
    const [endDate, setEndDate] = useState(new Date(appointmentData?.endDate));
    const [payingDate, setPayingDate] = useState(appointmentData?.payingDate ? new Date(appointmentData.payingDate) : new Date());
    const [article, setArticle] = useState(null);
    const [customer, setCustomer] = useState(null);
    const [worker, setWorker] = useState(null);
    const [technician, setTechnician] = useState(null);
    const [insuranceCompany, setInsuranceCompany] = useState(null);
    const [place, setPlace] = useState(null);
    const [isReservation, setIsReservation] = useState(false);
    const [isVestacenje, setIsVestacenje] = useState(false);
    const [isZaveden, setIsZaveden] = useState(false);
    const [isOnline, setIsOnline] = useState(false);
    const [isAbsent, setIsAbsent] = useState(false);
    const [isPayed, setIsPayed] = useState(false);
    const [customerAttended, setCustomerAttended] = useState(true);
    const [digitalRecordOpen, setDigitalRecordOpen] = useState(false);

    const [doctorPrice, setDoctorPrice] = useState(0);
    const [insuranceCompanyPrice, setInsuranceCompanyPrice] = useState(0);
    const [technicianPrice, setTechnicianPrice] = useState(0);
    const [articlePrice, setArticlePrice] = useState(0);
    const [init, setInit] = useState(true);

    // Customer Component shortcut
    const [openCustomerComponent, setOpenCustomerComponent] = useState(false);
    const handleClickOpenCustomerComponent = () => {
        setOpenCustomerComponent(true);
    };
    const handleCloseCustomerComponent = () => {
        setOpenCustomerComponent(false);
    };
    const initializeCustomer = (customer) => {
        setCustomer(customer);
        setValue('customer', customer)
        if (!appointmentData.worker && customer?.worker?.uuid) {
            const workerIri = `${API}${API_ROUTES.WORKERS}/${customer.worker.uuid}`;
            setWorker(workerIri);
        }
    };

    const handleStartDateChange = (e) => {
        setStartDate(e);
        setValue('startDate', e);
    }

    const handleEndDateChange = (e) => {
        setEndDate(e);
        setValue('endDate', e);
    }

    const handlePayingDateChange = (e) => {
        setPayingDate(e);
        setValue('payingDate', e);
    }

    const calculateCompanyPrice = (sumPrice, servicePrice) => {
        setValue('companyPrice', roundNumber(servicePrice - sumPrice), { shouldValidate : true });
    }

    const calculatePrices = (article, object, type, price = null) => {
        let workerUuid = '';
        if (object?.uuid) {
            workerUuid = object?.uuid;
        }
        else {
            workerUuid = object.replace('/api/workers/', '')
        }
        if (!init && workerUuid !== '' && article?.uuid) {
            let percentage;
            getData(`${ENTRYPOINT}${API_ROUTES.PERCENTAGES}?article=${article?.uuid}&worker=${workerUuid}`)
                .then((data) => {
                    if(data.length){
                        percentage = data[0].percentage/100;
                    }
                    else if (object.percentage) {
                        percentage = object.percentage / 100;
                    }
                    else if (type === 'doctor') {
                        percentage = 0.5;
                    }

                    let priceSum = 0;

                    let servicePrice = articlePrice;

                    if (price) {
                        servicePrice = price;
                    }

                    if(type === 'doctor'){
                        let doctorPriceNew = parseFloat(servicePrice * percentage);
                        if (isNaN(doctorPriceNew)) {
                            doctorPriceNew = 0;
                        }

                        setDoctorPrice(roundNumber(doctorPriceNew));
                        setValue('doctorPrice', roundNumber(doctorPriceNew), { shouldValidate : true });
                    }
                    if(type === 'insuranceCompany'){
                        let insuranceCompanyPriceNew = parseFloat(servicePrice  * percentage);
                        if (isNaN(insuranceCompanyPriceNew)) {
                            insuranceCompanyPriceNew = 0;
                        }
                        setInsuranceCompanyPrice(roundNumber(insuranceCompanyPriceNew));
                        setValue('insuranceCompanyPrice', roundNumber(insuranceCompanyPriceNew), { shouldValidate : false });
                    }
                    if(type === 'technician'){
                        let technicianPriceNew = parseFloat(servicePrice * percentage);
                        if (isNaN(technicianPriceNew)) {
                            technicianPriceNew = 0;
                        }
                        setTechnicianPrice(roundNumber(technicianPriceNew));
                        setValue('technicianPrice', roundNumber(technicianPriceNew), { shouldValidate : false });
                    }

                    priceSum = parseFloat(doctorPrice) + parseFloat(insuranceCompanyPrice) + parseFloat(technicianPrice);
                    calculateCompanyPrice(priceSum, servicePrice);
                });
        }
    }

    const handleArticleObject = (object) => {
        setArticle(object);
        setArticlePrice(object?.price);
        setValue('article', object, { shouldValidate : true });
        if (appointmentData?.price) {
            setValue('price', roundNumber(appointmentData.price), { shouldValidate : true });
        }
        else {
            setValue('price', roundNumber(object.price), { shouldValidate : true });
        }
        if (!appointmentData?.id && object?.duration) {
            const endDate = addMinutes(getValues('startDate'), object?.duration);
            setEndDate(endDate);
            setValue('endDate', endDate);
        }
    }

    const handlePriceChange = (e) => {
        const price = e.target.value;
        setValue('price', price, { shouldValidate : true });
        setArticlePrice(price);
        setValue('articlePrice', price)
    }

    const handleCustomerObject = (object) => {
        setCustomer(object);
        setValue('customer', object, {shouldValidate : true})
        if (!appointmentData.worker && object?.worker?.uuid) {
            const workerIri = `${API}${API_ROUTES.WORKERS}/${object.worker.uuid}`;
            setWorker(workerIri);
        }
    }

    const handleWorkerObject = (object) => {
        setWorker(object);
        setValue('worker', object, { shouldValidate: true });

        const type = 'doctor';
        calculatePrices(article, object, type);
    }

    const handleTechnicianObject = (object) => {
        setTechnician(object);
        setValue('technician', object, { shouldValidate: false });

        const type = 'technician'
        calculatePrices(article, object, type);
    }

    const handleInsuranceCompanyObject = (object) => {
        setInsuranceCompany(object);
        setValue('insuranceCompany', object, { shouldValidate: false });

        const type = 'insuranceCompany';
        calculatePrices(article, object, type);
    }

    const handlePlaceObject = (object) => {
        setPlace(object);
        setValue('place', object, { shouldValidate: true });
    }

    const handleCheckIsReservation = (e) => {
        setIsReservation(e.target.checked);
        setValue('isReservation', e.target.checked);
    }

    const handleCheckIsOnline = (e) => {
        setIsOnline(e.target.checked);
        setValue('isOnline', e.target.checked);
    }

    const handleCheckIsVestacenje = (e) => {
        setIsVestacenje(e.target.checked);
        setValue('isVestacenje', e.target.checked);
    }

    const handleCheckIsZaveden = (e) => {
        setIsZaveden(e.target.checked);
        setValue('isZaveden', e.target.checked);
    }

    const handleCheckCustomerAttended = (e) => {
        setCustomerAttended(e.target.checked);
        setValue('customerAttended', e.target.checked);
    }

    const handleCheckIsAbsent = (e) => {
        setIsAbsent(e.target.checked);
        setValue('isAbsent', e.target.checked);
    }

    const handleCheckIsPayed = (e) => {
        setIsPayed(e.target.checked);
        setValue('isPayed', e.target.checked);
        if(e.target.checked){
            setValue('payingDate', new Date());
            setPayingDate(new Date());
        }
        else {
            setValue('payingDate', null);
            setPayingDate(null);
        }
    }

    const handleClickDigitalRecord = () => {
        setDigitalRecordOpen(true);
    }

    React.useEffect(() => {
        if (article) {
            if (worker) {
                calculatePrices(article, worker, 'doctor');
            }
            if (technician) {
                calculatePrices(article, technician, 'technician');
            }
            if (insuranceCompany) {
                calculatePrices(article, insuranceCompany, 'insuranceCompany');
            }
        }
    }, [articlePrice, doctorPrice, technicianPrice, insuranceCompanyPrice])

    useEffect(() => {
        if (appointmentData?.id) {
            setValue('id', appointmentData?.id);
        }
        else {
            let startDate = appointmentData?.startDate;
            let endDate = appointmentData?.endDate;
            if (currentViewName === 'Month') {
                const { hours } = getCurrentTime();
                startDate = set(startDate, { hours, minutes: 0, seconds: 0 });
                endDate = set(startDate, { hours, minutes: 30, seconds: 0 });
            }
            setStartDate(startDate);
            setEndDate(endDate);
            setValue('startDate', startDate);
            setValue('endDate', endDate);
        }
        if (appointmentData?.startDate) {
            if (appointmentData?.id) {
                setStartDate(new Date(appointmentData?.startDate));
                setValue('startDate', appointmentData?.startDate);
            }
        }
        if (appointmentData?.endDate) {
            if (appointmentData?.id) {
                setEndDate(new Date(appointmentData?.endDate));
                setValue('endDate', appointmentData?.endDate);
            }
        }
        if (appointmentData?.article) {
            setArticle(appointmentData?.article);
            setValue('article', appointmentData?.article);
        }
        if (appointmentData?.price) {
            setValue('price', appointmentData?.price);
        }
        if (appointmentData?.companyPrice) {
            setValue('companyPrice', appointmentData?.companyPrice);
        }
        if (appointmentData?.doctorPrice) {
            setValue('doctorPrice', appointmentData?.doctorPrice);
        }
        if (appointmentData?.insuranceCompanyPrice) {
            setValue('insuranceCompanyPrice', appointmentData?.insuranceCompanyPrice);
        }
        if (appointmentData?.technicianPrice) {
            setValue('technicianPrice', appointmentData?.technicianPrice);
        }
        if (appointmentData?.customer) {
            setCustomer(appointmentData?.customer);
            setValue('customer', appointmentData?.customer);
        }
        if (appointmentData?.worker) {
            setWorker(appointmentData?.worker);
            setValue('worker', appointmentData?.worker);
        }
        if (appointmentData?.technician) {
            setTechnician(appointmentData?.technician);
            setValue('technician', appointmentData?.technician);
        }
        if (appointmentData?.insuranceCompany) {
            setInsuranceCompany(appointmentData?.insuranceCompany);
            setValue('insuranceCompany', appointmentData?.insuranceCompany);
        }
        if (appointmentData?.place) {
            setPlace(appointmentData?.place);
            setValue('place', appointmentData?.place);
        }
        // If grouped by place, set the place to be of the selected group
        else if (!appointmentData?.place && appointmentData.placeUuid && appointmentData.placeUuid !== -1) {
            const place = `${API}${API_ROUTES.PLACES}/${appointmentData.placeUuid}`;
            setPlace(place);
            setValue('place', place);
        }
        if (appointmentData?.isReservation) {
            setValue('isReservation', appointmentData?.isReservation);
            setIsReservation(appointmentData?.isReservation);
        }
        if (appointmentData?.isOnline) {
            setValue('isOnline', appointmentData?.isOnline);
            setIsOnline(appointmentData?.isOnline);
        }
        if (appointmentData?.isVestacenje) {
            setValue('isVestacenje', appointmentData?.isVestacenje);
            setIsVestacenje(appointmentData?.isVestacenje);
        }
        if (appointmentData?.isZaveden) {
            setValue('isZaveden', appointmentData?.isZaveden);
            setIsZaveden(appointmentData?.isZaveden);
        }
        if (appointmentData?.isAbsent) {
            setValue('isAbsent', appointmentData?.isAbsent);
            setIsAbsent(appointmentData?.isAbsent);
        }
        if (appointmentData?.isPayed) {
            setValue('isPayed', appointmentData?.isPayed);
            setIsPayed(appointmentData?.isPayed);
        }
        if (appointmentData?.payingDate) {
            setValue('payingDate', appointmentData?.payingDate);
        }
        if (appointmentData?.description) {
            setValue('description', appointmentData?.description);
        }
        if (appointmentData?.customerAttended !== null) {
            setValue('customerAttended', appointmentData?.customerAttended);
            setCustomerAttended(appointmentData?.customerAttended);
        }
        setTimeout(() => {
            setInit(false);
        }, 2000);
    }, []);

    useEffect(() => {
        return () => {
            reset({ id: null, price: '', description: '' });
        }
    }, []);

	return (
        <Container className={container}>
            <Grid container justify="center" spacing={1}>
                <Grid item xs={12} sm={6} md={4}>
                    <DateTimePicker
                        disabled={isDoctor}
                        name="startDate"
                        control={control}
                        rules={{ required:  'Unesite početak termina', validate: date => validateDate(date) }}
                        value={startDate}
                        onChange={handleStartDateChange}
                        label="Početak termina"
                        placeholder="Početak termina"
                    />
                    <ErrorMessage errors={errors} name="startDate" as="p" />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <DateTimePicker
                        disabled={isDoctor}
                        name="endDate"
                        control={control}
                        rules={{ required:  'Unesite kraj termina', validate: date => validateDate(date) }}
                        value={endDate}
                        onChange={handleEndDateChange}
                        label="Kraj termina"
                        placeholder="Kraj termina"
                    />
                    <ErrorMessage errors={errors} name="endDate" as="p" />
                </Grid>
                <Grid item xs={12} sm={appointmentData.customer ? 8 : 12} md={appointmentData.customer ? 2 : 4}>
                    <Autocomplete
                        disabled={isDoctor}
                        name="place"
                        label="Mesto"
                        placeholder="Mesto"
                        control={control}
                        api={API_ROUTES.PLACES}
                        handleObject={handlePlaceObject}
                        preloadValue={place}
                    />
                    <ErrorMessage errors={errors} name="place" as="p" />
                </Grid>
                {appointmentData.customer &&
                    <Grid item xs={12} sm={4} md={2}>
                    <IconButton color="primary" classes={{ label: iconButtonLabel }} onClick={() => handleClickDigitalRecord()}>
                        <RecentActors fontSize="default" />
                        <Typography variant="caption" gutterBottom>
                            Digitalni karton
                        </Typography>
                    </IconButton>
                </Grid>
                }

                <Grid item xs={12} sm={8}>
                    <Autocomplete
                        disabled={isDoctor}
                        name="article"
                        label="Usluga"
                        placeholder="Usluga"
                        control={control}
                        rules={{ required:  'Unesite uslugu' }}
                        api={API_ROUTES.ARTICLES}
                        handleObject={handleArticleObject}
                        preloadValue={article}
                    />
                    <ErrorMessage errors={errors} name="article" as="p" />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextInput
                        disabled={isDoctor}
                        name="price"
                        control={control}
                        label="Cena"
                        type="number"
                        rules={{ required: 'Unesite cenu usluge' }}
                        onBlur={handlePriceChange}
                    />
                    <ErrorMessage errors={errors} name="price" as="p" />
                </Grid>

                <Grid item xs={12} md={4} className={inlineFlex}>
                    <Autocomplete
                        disabled={isDoctor}
                        name="customer"
                        label="Klijent"
                        placeholder="Klijent"
                        control={control}
                        api={API_ROUTES.CUSTOMERS}
                        handleObject={handleCustomerObject}
                        preloadValue={customer}
                    />
                    <IconButton className={iconButton} onClick={handleClickOpenCustomerComponent}>
                        <AddBox
                            color="primary"
                            fontSize="large"
                        />
                    </IconButton>
                    <ErrorMessage errors={errors} name="customer" as="p" />
                </Grid>
                <Grid item xs={12} md={4}>
                    <Autocomplete
                        disabled={isDoctor}
                        name="worker"
                        label="Radnik"
                        placeholder="Radnik"
                        control={control}
                        api={API_ROUTES.WORKERS}
                        handleObject={handleWorkerObject}
                        preloadValue={worker}
                    />
                    <ErrorMessage errors={errors} name="worker" as="p" />
                </Grid>
                <Grid item xs={12} md={4}>
                    <Autocomplete
                        disabled={isDoctor}
                        name="technician"
                        label="Tehničar"
                        placeholder="Tehničar"
                        control={control}
                        api={API_ROUTES.WORKERS}
                        handleObject={handleTechnicianObject}
                        preloadValue={technician}
                    />
                    <ErrorMessage errors={errors} name="worker" as="p" />
                </Grid>

                <Grid item xs={12} lg={4}>
                    <Autocomplete
                        disabled={isDoctor}
                        name="insuranceCompany"
                        label="Osiguravajuća kuća"
                        placeholder="Osiguravajuća kuća"
                        control={control}
                        api={API_ROUTES.WORKERS}
                        handleObject={handleInsuranceCompanyObject}
                        preloadValue={insuranceCompany}
                    />
                    <ErrorMessage errors={errors} name="worker" as="p" />
                </Grid>
                <Grid item xs={12} md={3} lg={2}>
                    <TextInput
                        disabled={isDoctor}
                        name="companyPrice"
                        control={control}
                        label="Cena kompanije"
                        type="number"
                        rules={{ required: 'Unesite cenu usluge za kompaniju' }}
                    />
                    <ErrorMessage errors={errors} name="companyPrice" as="p" />
                </Grid>
                <Grid item xs={12} md={3} lg={2}>
                    <TextInput
                        disabled={isDoctor}
                        name="doctorPrice"
                        control={control}
                        label="Cena doktora"
                        type="number"
                        // rules={{ required: 'Unesite cenu usluge za doktora' }}
                    />
                    {/* <ErrorMessage errors={errors} name="doctorPrice" as="p" /> */}
                </Grid>
                <Grid item xs={12} md={3} lg={2}>
                    <TextInput
                        disabled={isDoctor}
                        name="technicianPrice"
                        control={control}
                        label="Cena tehničara"
                        type="number"
                        // rules={{ required: 'Unesite cenu usluge za tehničara' }}
                    />
                    {/* <ErrorMessage errors={errors} name="technicianPrice" as="p" /> */}
                </Grid>
                <Grid item xs={12} md={3} lg={2} className={lsSm}>
                    <TextInput
                        disabled={isDoctor}
                        name="insuranceCompanyPrice"
                        control={control}
                        label="Cena osiguravajuće kuće"
                        type="number"
                        // rules={{ required: 'Unesite cenu usluge za osiguravajuću kuću' }}
                    />
                    {/* <ErrorMessage errors={errors} name="insuranceCompanyPrice" as="p" /> */}
                </Grid>

                <Grid item xs={12}>
                    <TextInput
                        disabled={isDoctor}
                        name="description"
                        control={control}
                        label="Opis"
                        variant="outlined"
                        multiline={true}
                        rows={5}
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <CheckboxInput
                        disabled={isDoctor}
                        name="isPayed"
                        control={control}
                        label="Plaćeno?"
                        value={isPayed}
                        onChange={handleCheckIsPayed}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <CheckboxInput
                        disabled={isDoctor}
                        name="customerAttended"
                        control={control}
                        label="Klijent je bio prisutan?"
                        value={customerAttended}
                        onChange={handleCheckCustomerAttended}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <CheckboxInput
                        disabled={isDoctor}
                        name="isAbsent"
                        control={control}
                        label="Odsutan?"
                        value={isAbsent}
                        onChange={handleCheckIsAbsent}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <CheckboxInput
                        disabled={isDoctor}
                        name="isReservation"
                        control={control}
                        label="Rezervacija?"
                        value={isReservation}
                        onChange={handleCheckIsReservation}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <CheckboxInput
                        disabled={isDoctor}
                        name="isOnline"
                        control={control}
                        label="Online?"
                        value={isOnline}
                        onChange={handleCheckIsOnline}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <CheckboxInput
                        disabled={isDoctor}
                        name="isVestacenje"
                        control={control}
                        label="Veštačenje?"
                        value={isVestacenje}
                        onChange={handleCheckIsVestacenje}
                    />
                </Grid>
                <Grid item xs={12} md={8}>
                    <CheckboxInput
                        disabled={isDoctor}
                        name="isZaveden"
                        control={control}
                        label="Zaveden?"
                        value={isZaveden}
                        onChange={handleCheckIsZaveden}
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    {isPayed &&
                        <DatePicker
                            disabled={isDoctor}
                            name="payingDate"
                            control={control}
                            value={payingDate}
                            onChange={handlePayingDateChange}
                            label="Datum plaćanja"
                            placeholder="Datum plaćanja"
                        />
                    }
                </Grid>
            </Grid>

            <FormShortcut
                component={CustomerComponent}
                open={openCustomerComponent}
                handleClose={handleCloseCustomerComponent}
                initialize={initializeCustomer}
            />
            <DigitalRecordModal
                customerUuid={appointmentData.customer && appointmentData.customer.uuid }
                customerName={appointmentData.customer && `${appointmentData.customer.firstName} ${appointmentData.customer.lastName}`}
                customerBirthYear={appointmentData.customer && appointmentData.customer.customerBirthYear}
                open={digitalRecordOpen}
                setOpen={setDigitalRecordOpen}
            />
        </Container>
	);
};

BasicLayout.propTypes = {
    appointmentData: PropTypes.object,
    currentViewName: PropTypes.string,
    isDoctor: PropTypes.bool
}

export default BasicLayout;

